import { useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, useState, type Ref } from 'react';
import { Button } from '../../shared/ui/components';

/**
 * Cuadro de escritura del Cuaderno que muestra el subrayado, el resaltado y las notas tal cual,
 * sin símbolos. Por dentro se guarda en el MISMO formato de texto de siempre (ver noteMarkup.tsx):
 *   __subrayado__   ==resaltado==   {{nota}}   y líneas de pasaje que empiezan con «>»
 * así los estudios ya guardados se abren igual.
 */

const INLINE = /(__[^_\n]+?__|==[^=\n]+?==|\{\{[^}\n]+?\}\})/g;
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function inlineHtml(text: string): string {
  return text
    .split(INLINE)
    .map((part) => {
      if (part.startsWith('__') && part.endsWith('__') && part.length > 4) return `<u>${inlineHtml(part.slice(2, -2))}</u>`;
      if (part.startsWith('==') && part.endsWith('==') && part.length > 4) return `<mark>${inlineHtml(part.slice(2, -2))}</mark>`;
      if (part.startsWith('{{') && part.endsWith('}}') && part.length > 4) return noteHtml(part.slice(2, -2).trim());
      return esc(part);
    })
    .join('');
}

const noteHtml = (text: string) => `<span class="note-annot" contenteditable="false" data-note="${esc(text)}" role="note">📝 ${esc(text)}</span>`;
const QUOTE_MARK = '<span class="note-quote-mark" contenteditable="false">&gt; </span>';

/** Texto del cuaderno → HTML del editor: una línea por <div>. */
export function markupToHtml(text: string): string {
  return text
    .split('\n')
    .map((line) => {
      if (line.startsWith('>')) {
        const rest = line.replace(/^>\s?/, '');
        return `<div class="note-line note-line--quote">${QUOTE_MARK}${inlineHtml(rest) || '<br>'}</div>`;
      }
      return `<div class="note-line">${inlineHtml(line) || '<br>'}</div>`;
    })
    .join('');
}

const BLOCKS = new Set(['DIV', 'P', 'BLOCKQUOTE', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'PRE']);

/** Lo de adentro de un subrayado o resaltado: solo texto y marcas, en una sola línea. */
function inlineMarkup(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').replace(/​/g, '').replace(/\n/g, ' ');
  if (!(node instanceof HTMLElement)) return '';
  if (node.tagName === 'BR') return ' ';
  if (node.classList.contains('note-quote-mark')) return '';
  if (node.dataset.note !== undefined) {
    const t = node.dataset.note.replace(/[{}\n]/g, '').trim();
    return t ? `{{${t}}}` : '';
  }
  const inner = Array.from(node.childNodes).map(inlineMarkup).join('');
  if (node.tagName === 'U' || node.tagName === 'INS') return inner.trim() && !inner.includes('_') ? `__${inner}__` : inner;
  if (node.tagName === 'MARK') return inner.trim() && !inner.includes('=') ? `==${inner}==` : inner;
  return inner;
}

/** HTML del editor → texto del cuaderno (el mismo formato que ya se guardaba). */
export function domToMarkup(root: HTMLElement): string {
  const lines: string[] = [];
  let cur: string | null = null;
  const endLine = () => {
    lines.push(cur ?? '');
    cur = null;
  };
  const visit = (node: Node, isLast: boolean, lineStart: number) => {
    if (node instanceof HTMLElement && BLOCKS.has(node.tagName)) {
      if (cur !== null) endLine();
      const before = lines.length;
      const kids = Array.from(node.childNodes);
      kids.forEach((k, i) => visit(k, i === kids.length - 1, before));
      if (cur !== null) endLine();
      else if (lines.length === before) lines.push('');
      return;
    }
    if (node instanceof HTMLElement && node.tagName === 'BR') {
      // El <br> final de un bloque solo sostiene la línea; si la línea ya terminó, es una línea vacía.
      if (!isLast) endLine();
      else if (cur === null && lines.length > lineStart) lines.push('');
      return;
    }
    if (node instanceof HTMLElement && node.classList.contains('note-quote-mark')) {
      cur = `${cur ?? ''}> `;
      return;
    }
    cur = (cur ?? '') + inlineMarkup(node);
  };
  const kids = Array.from(root.childNodes);
  kids.forEach((k, i) => visit(k, i === kids.length - 1, 0));
  if (cur !== null) endLine();
  // Una línea de pasaje que quedó vacía («> ») vuelve a ser «>».
  return lines.map((l) => (l === '> ' ? '>' : l)).join('\n');
}

export type NoteEditorHandle = {
  /** Inserta líneas (por ejemplo un pasaje) después de la línea donde está el cursor y deja el cursor debajo. */
  insertBlock: (markup: string) => void;
};

type Props = {
  initial: string;
  onChange: (markup: string) => void;
  onMessage: (message: string | null) => void;
  ref?: Ref<NoteEditorHandle>;
};

export function NoteEditor({ initial, onChange, onMessage, ref }: Props) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const saved = useRef<Range | null>(null);
  const [noteFor, setNoteFor] = useState<{ range: Range; label: string } | null>(null);
  const [noteText, setNoteText] = useState('');

  useLayoutEffect(() => {
    if (root.current) root.current.innerHTML = markupToHtml(initial);
    // Solo al abrir: después el cuadro es la fuente del texto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inside = (n: Node | null) => Boolean(n && root.current && root.current.contains(n));
  useEffect(() => {
    const keep = () => {
      const s = document.getSelection();
      if (s && s.rangeCount && inside(s.getRangeAt(0).commonAncestorContainer)) saved.current = s.getRangeAt(0).cloneRange();
    };
    document.addEventListener('selectionchange', keep);
    return () => document.removeEventListener('selectionchange', keep);
  }, []);

  const current = (): Range | null => {
    const s = document.getSelection();
    if (s && s.rangeCount && inside(s.getRangeAt(0).commonAncestorContainer)) return s.getRangeAt(0);
    return saved.current && inside(saved.current.commonAncestorContainer) ? saved.current : null;
  };
  const sync = () => {
    const el = root.current;
    if (!el) return;
    for (const line of Array.from(el.children)) line.classList.toggle('note-line--quote', Boolean(line.firstElementChild?.classList.contains('note-quote-mark')));
    onChange(domToMarkup(el));
  };
  const place = (node: Node, after = true) => {
    const r = document.createRange();
    if (after) r.setStartAfter(node);
    else r.setStart(node, 0);
    r.collapse(true);
    const s = document.getSelection();
    s?.removeAllRanges();
    s?.addRange(r);
    saved.current = r.cloneRange();
  };

  /** La línea (hijo directo del cuadro) que contiene el nodo. */
  const lineOf = (n: Node | null): ChildNode | null => {
    let x: Node | null = n;
    while (x && x.parentNode !== root.current) x = x.parentNode;
    return x as ChildNode | null;
  };

  function toggle(tag: 'u' | 'mark') {
    const r = current();
    if (!r || r.collapsed) return onMessage('Primero selecciona el texto que quieres marcar.');
    const owner = (n: Node) => (n instanceof Element ? n : n.parentElement)?.closest(tag) ?? null;
    const a = owner(r.startContainer);
    if (a && inside(a) && a === owner(r.endContainer)) {
      // Ya estaba marcado: quitar la marca.
      a.replaceWith(...Array.from(a.childNodes));
      onMessage(null);
      return sync();
    }
    const probe = r.cloneContents();
    if (probe.querySelector('div, p, br')) return onMessage('Marca el texto de una sola línea a la vez.');
    const el = document.createElement(tag);
    el.appendChild(r.extractContents());
    r.insertNode(el);
    place(el);
    onMessage(null);
    sync();
  }

  function startNote() {
    const r = current();
    if (!r) return onMessage('Pon el cursor donde va la nota.');
    onMessage(null);
    setNoteFor({ range: r.cloneRange(), label: r.toString() });
  }
  function addNote() {
    const text = noteText.trim().replace(/[{}]/g, '');
    if (!noteFor || !text) return;
    const r = noteFor.range.cloneRange();
    r.collapse(false);
    const wrap = document.createElement('span');
    wrap.innerHTML = noteHtml(text);
    const note = wrap.firstChild!;
    r.insertNode(note);
    note.parentNode!.insertBefore(document.createTextNode(' '), note);
    place(note);
    setNoteFor(null);
    setNoteText('');
    sync();
  }

  useImperativeHandle(ref, () => ({
    insertBlock(markup: string) {
      const el = root.current;
      if (!el) return;
      const holder = document.createElement('div');
      holder.innerHTML = markupToHtml(`${markup}\n`);
      const lines = Array.from(holder.childNodes);
      const r = current();
      const line = r ? lineOf(r.endContainer) : null;
      // Si la línea actual está vacía, el pasaje la ocupa.
      const empty = line && (line.textContent ?? '') === '' && !(line instanceof Element && line.querySelector('[data-note]'));
      if (line && empty) line.replaceWith(...lines);
      else if (line) line.after(...lines);
      else el.append(...lines);
      const last = lines.at(-1)!;
      place(last, false);
      sync();
    },
  }));

  const tools = (
    <div className="note-toolbar" role="toolbar" aria-label="Formato">
      <button type="button" className="btn btn--ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => toggle('u')} title="Subrayar la selección">
        <u>S</u> Subrayar
      </button>
      <button type="button" className="btn btn--ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => toggle('mark')} title="Resaltar la selección">
        <mark>R</mark> Resaltar
      </button>
      <button type="button" className="btn btn--ghost" onMouseDown={(e) => e.preventDefault()} onClick={startNote} title="Agregar una nota donde está el cursor">
        📝 Nota
      </button>
    </div>
  );

  return (
    <div className="field">
      <label id={`${id}-label`} onClick={() => root.current?.focus()}>
        Notas
      </label>
      {tools}
      <div
        ref={root}
        id={`${id}-box`}
        className="note-editor note-body"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-hint`}
        onInput={sync}
        onBlur={sync}
        onPaste={(e) => {
          // Solo texto: el formato se pone con los botones.
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          if (!document.execCommand?.('insertText', false, text)) {
            const r = current();
            if (r) {
              r.deleteContents();
              const t = document.createTextNode(text);
              r.insertNode(t);
              place(t);
            }
          }
          sync();
        }}
        onKeyDown={(e) => {
          // Enter crea una línea nueva (un <div>), igual en todos los navegadores.
          if (e.key === 'Enter' && !e.shiftKey) document.execCommand?.('defaultParagraphSeparator', false, 'div');
        }}
      />
      <small id={`${id}-hint`} className="field__hint">
        Selecciona texto y toca Subrayar o Resaltar (tócalo otra vez para quitarlo). Inserta pasajes arriba y sigue escribiendo.
      </small>
      {noteFor ? (
        <div className="family-add">
          <div className="field">
            <label htmlFor={`${id}-note`}>Nota {noteFor.label ? `sobre «${noteFor.label.slice(0, 40)}${noteFor.label.length > 40 ? '…' : ''}»` : 'en este punto'}</label>
            <input
              id={`${id}-note`}
              value={noteText}
              maxLength={500}
              autoFocus
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addNote();
                }
              }}
            />
          </div>
          <Button type="button" variant="secondary" onClick={addNote}>
            Agregar nota
          </Button>
          <Button type="button" variant="ghost" onClick={() => setNoteFor(null)}>
            Cancelar
          </Button>
        </div>
      ) : null}
    </div>
  );
}
